import { useMutation } from '@apollo/client'
import { FormModal, useColorMode } from '@chaine/keychaine'
import { AppTypeEnum } from '@chaine/types'
import React, { useRef, useState } from 'react'
import { SubmitHandler } from 'react-hook-form'
import { v4 as uuidv4 } from 'uuid'
import * as Yup from 'yup'

import { Toast } from '../../../_shared/components/toast'
import { AppModalProps } from '../components/apps-data'
import { TCustomer, TUpdateFourKitesAppInput, UPDATE_FOUR_KITES_APP } from '../data/mutations'
import {
  baseValidationSchema,
  endsWithUUID,
  getGroupStructure,
  getInitialValues,
  getPayloadStructure,
  validationSchema
} from '../utils/four-kites-modal-utils'
import { ModalFooter } from './modal-footer'

type TFieldValues = { [key: string]: string; apiKey: string }

/**
 * Updates the four kites existing configuration for further adding and deleting customers
 */
const FourKitesCarrierUpdateModal = React.memo((props: AppModalProps) => {
  const [schema, setSchema] = useState(baseValidationSchema.concat(validationSchema(1)))
  const { appType, isOpen, refetchApps, onClose, isModalEditable, setModalEditable, app } = props
  const [formError, setFormError] = useState('')
  const { colorMode } = useColorMode()
  const { showToast } = Toast()
  const [customers, setCustomers] = useState<Record<string, Record<string, string | boolean>[]> | undefined>(
    app?.workspaceApp?.appMetadata?.customers?.reduce((group, customer, index) => {
      return {
        ...group,
        ...getGroupStructure(index + 1, isModalEditable, customer.integrationConfigurationID)
      }
    }, {})
  )
  const deletedGroups = useRef<string[]>([])
  const existingDeleteGroups = useRef<Record<string, string>[]>([])
  const alreadyCreated = useRef<string[]>([])
  const updates = useRef<{ created: TCustomer[]; deleted: TCustomer[] }>({
    created: [],
    deleted: []
  })

  const updateSchema = (uniqueID: string | number) => {
    setSchema((prevSchema) => prevSchema.concat(validationSchema(uniqueID)))
  }

  function resetFields() {
    deletedGroups.current = []
    existingDeleteGroups.current = []
    alreadyCreated.current = []
    updates.current.created = []
    updates.current.deleted = []
    if (setModalEditable) {
      setModalEditable(false)
    }
  }

  const removeFieldsFromSchema = (id: string) => {
    setSchema((prevSchema) => {
      deletedGroups.current.push(id)
      const currentFields = prevSchema.fields
      const filteredFields = Object.keys(currentFields)
        .filter((key) => ![`customerID-${id}`, `customerIDInChaine-${id}`, `customerNameInChaine-${id}`].includes(key))
        .reduce((acc, key) => {
          acc[key] = currentFields[key]
          return acc
        }, {} as Record<string, Yup.AnySchema>)
      return Yup.object(filteredFields) as typeof prevSchema
    })
  }

  const handleAddMore = () => {
    const uniqueID = uuidv4()
    const newlyAddedGroup = getGroupStructure(uniqueID)
    updateSchema(uniqueID)
    setCustomers((old) => {
      return {
        ...old,
        ...newlyAddedGroup
      } as typeof old
    })
  }

  const closeModal = () => {
    setFormError('')
    onClose()
    if (setModalEditable) {
      setModalEditable(false)
    }
  }

  const handleGroupDelete = (groupID: string) => {
    if (customers && Object.keys(customers).length > 1) {
      if (customers[groupID][0].disabled) {
        existingDeleteGroups.current.push({
          id: groupID,
          integrationConfigurationID: customers[groupID][0].integrationConfigurationID as string
        })
      }
      removeFieldsFromSchema(groupID)
      setCustomers((old) => {
        const newGroups: Record<string, Record<string, string | boolean>[]> = { ...old }
        delete newGroups[groupID]
        return newGroups
      })
    }
  }

  const [updateFourKitesApp, { loading: updating }] = useMutation<
    {
      updateCustomersInFourKitesCarrier: {
        apiKey: string
        appType: AppTypeEnum.FourKitesCarrier
        updates: { created: TCustomer[]; deleted: TCustomer[] }
      }
    },
    TUpdateFourKitesAppInput
  >(UPDATE_FOUR_KITES_APP, {
    onCompleted: () => {
      showToast({
        status: 'success',
        title: `FourKites app is updated successfully!`
      })
      refetchApps()
      onClose()
      setFormError('')
      resetFields()
    },
    onError: (err) => {
      if (err.message === 'Incorrect api key') {
        setFormError('Invalid API Key. Please try again.')
        showToast({
          status: 'error',
          title: `Invalid API Key. Please try again.`
        })
      } else {
        setFormError(`Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`)
        showToast({
          status: 'error',
          title: `Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`
        })
      }
    }
  })

  const handleSaveDetails: SubmitHandler<TFieldValues> = (event) => {
    // It deletes existing groups from event data to keep the schema and data in sync
    if (existingDeleteGroups.current.length > 0) {
      existingDeleteGroups.current.forEach((record) => {
        if (
          updates.current.deleted.filter((ele) => {
            return event[`customerID-${record.id}`] === ele.customerID
          }).length === 0
        ) {
          updates.current.deleted.push({
            customerID: event[`customerID-${record.id}`],
            customerIDInChaine: event[`customerIDInChaine-${record.id}`],
            customerNameInChaine: event[`customerNameInChaine-${record.id}`],
            integrationConfigurationID: record.integrationConfigurationID
          })
        }
        delete event[`customerID-${record.id}`]
        delete event[`customerIDInChaine-${record.id}`]
        delete event[`customerNameInChaine-${record.id}`]
      })
    }

    // It deletes recently added groups from event data to keep the schema and data in sync
    if (deletedGroups.current.length > 0) {
      deletedGroups.current.forEach((id) => {
        delete event[`customerID-${id}`]
        delete event[`customerIDInChaine-${id}`]
        delete event[`customerNameInChaine-${id}`]
      })
    }

    Object.keys(event).forEach((key) => {
      if (!endsWithUUID(key) && key !== 'apiKey' && !alreadyCreated.current.includes(key)) {
        alreadyCreated.current.push(key)
        delete event[key]
      }
    })

    if (getPayloadStructure(event).customers?.length > 0) {
      updates.current.created.push(...getPayloadStructure(event).customers)
    }

    updateFourKitesApp({
      variables: {
        apiKey: event.apiKey || app?.workspaceApp?.appCredentials?.apiKey || '',
        appType,
        updates: {
          created: updates.current.created,
          deleted: updates.current.deleted
        }
      }
    })
  }

  return (
    <>
      <FormModal
        isOpen={isOpen}
        onClose={closeModal}
        size="3xl"
        title={`Update FourKites for Chaine`}
        subtitle={`Updating this integration requires the workspace and customer ids.`}
        formSubmitHandler={handleSaveDetails}
        buttonText={'Update'}
        initialValues={getInitialValues({
          apiKey: app?.workspaceApp?.appCredentials?.apiKey || '',
          customers: app?.workspaceApp?.appMetadata?.customers || []
        })}
        validationSchema={schema}
        isLoading={updating}
        footer={formError ? <ModalFooter formError={formError} /> : <></>}
        addMoreHandler={handleAddMore}
        onGroupDelete={handleGroupDelete}
        modalBodyProps={{ maxHeight: 600, overflow: 'auto' }}
        modalItems={[
          {
            isRequired: true,
            labelText: 'API Key',
            name: 'apiKey',
            placeHolder: 'Enter an API Key',
            type: 'input'
          },
          {
            direction: 'column',
            /* eslint-disable @typescript-eslint/ban-ts-comment */
            // @ts-ignore
            inputGroups: customers,
            labelText: 'Customer Ids',
            name: 'inputGroups',
            otherProps: {
              backgroundColor: colorMode === 'dark' ? 'black' : 'gray.50',
              borderRadius: 12,
              gap: 2,
              padding: 4
            },
            type: 'inputGroups'
          }
        ]}
      />
    </>
  )
})

FourKitesCarrierUpdateModal.displayName = 'FourKitesCarrierUpdateModal'

export { FourKitesCarrierUpdateModal }
